import type { IControlCard } from '../../fhir/interfaces/fhir-converter.interfaces';
import { defaultOtherInputQuestionTitle } from './other-input-title';

const OTHER_CAPABLE_CONTROL_TYPES = new Set([
  'radio-group',
  'checkbox-group',
  'dropdown',
  'dropdown-with-label',
  'select',
]);

const isOtherInputChild = (control: IControlCard | undefined): boolean =>
  !!(control as any)?.__isOtherInput || !!(control?.componentProps as any)?.__isOtherInput;

/** Copies parent-level "Other" settings (and the default Question Title) onto the child; never overwrites a value. */
export function applyLegacyOtherInputSettings(
  childProps: Record<string, any>,
  parentProps: Record<string, any>,
  parentLabel?: unknown
): void {
  const legacyPlaceholder =
    parentProps['otherRadioPlaceholder'] ??
    parentProps['otherCheckboxPlaceholder'] ??
    parentProps['otherDropdownPlaceholder'] ??
    parentProps['otherPlaceholder'] ??
    parentProps['reasonPlaceholder'];
  if (!childProps['placeholder'] && typeof legacyPlaceholder === 'string' && legacyPlaceholder.trim()) {
    childProps['placeholder'] = legacyPlaceholder;
  }
  if (childProps['multiline'] === undefined && typeof parentProps['otherMultiline'] === 'boolean') {
    childProps['multiline'] = parentProps['otherMultiline'];
  }
  const legacyLabel = parentProps['reasonLabel'];
  if (!childProps['label'] && typeof legacyLabel === 'string' && legacyLabel.trim()) {
    childProps['label'] = legacyLabel;
  }
  if (!childProps['questionTitle']) {
    childProps['questionTitle'] = defaultOtherInputQuestionTitle(
      parentProps['questionTitle'],
      parentLabel ?? parentProps['label']
    );
  }
}

/**
 * Brings a radio/checkbox control's "Other" input up to the current format in place: ensures the Other text-input child
 * exists and carries the settings that used to live on the parent. Idempotent.
 */
export function migrateOtherInputControl(parent: IControlCard, toControlId: (linkId: string) => string): void {
  if (!OTHER_CAPABLE_CONTROL_TYPES.has(String(parent.controlType ?? (parent as any).type))) {
    return;
  }

  const parentProps = ((parent.componentProps as Record<string, any> | undefined) ??
    ((parent as any).componentProps = {})) as Record<string, any>;
  const parentLinkId = (parent as any).linkId as string | undefined;
  const options: any[] = Array.isArray(parentProps['options']) ? parentProps['options'] : [];
  const otherOption = options.find(
    (opt) =>
      opt &&
      (opt.id === 'other' || String(opt.value).toLowerCase() === 'other' || String(opt.label).toLowerCase() === 'other')
  );
  let child = (parent.children ?? []).find((c) => isOtherInputChild(c));

  const hasOther =
    !!child ||
    !!otherOption ||
    parentProps['hasOther'] === true ||
    parentProps['showOtherOption'] === true ||
    parentProps['showOtherInput'] === true ||
    (parent as any).hasOther === true;
  if (!hasOther || !parentLinkId) {
    return;
  }

  if (!child) {
    const optionValue = String(otherOption?.value ?? 'other');
    const linkId = `${parentLinkId}.other`;
    child = {
      id: toControlId(linkId),
      controlType: 'text-input',
      linkId,
      icon: '',
      question: '',
      isRequired: false,
      hasOptions: false,
      __parentOptionId: optionValue,
      __parentLinkId: parentLinkId,
      __isOtherInput: true,
      componentProps: { __parentOptionId: optionValue, __parentLinkId: parentLinkId, __isOtherInput: true },
    } as unknown as IControlCard;
    parent.children = [...(parent.children ?? []), child];
    (parent as any).otherInputLinkId = linkId;
  }

  const childProps = ((child.componentProps as Record<string, any> | undefined) ??
    ((child as any).componentProps = {})) as Record<string, any>;
  applyLegacyOtherInputSettings(childProps, parentProps, (parent as any).question);
  (child as any).questionTitle = childProps['questionTitle'];
  if (childProps['label']) {
    (child as any).label = childProps['label'];
  }
}

/** Migrates every radio/checkbox in a control tree (subsections, tab groups, nested children) in place. */
export function migrateOtherInputTree(
  controls: IControlCard[] | undefined,
  toControlId: (linkId: string) => string
): void {
  for (const control of controls ?? []) {
    if (!control) continue;
    migrateOtherInputControl(control, toControlId);
    // Skip the Other child itself; it has no nested controls to migrate.
    if (control.children?.length) {
      migrateOtherInputTree(
        control.children.filter((child) => !isOtherInputChild(child)),
        toControlId
      );
    }
  }
}
